// PrivacyPolicy.js
import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <h1 className="text-4xl font-bold text-center mb-8">Privacy Policy for Baby Names App</h1>
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-sm text-gray-600 mb-6">Last updated: January 08, 2025</div>
          
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-gray-700">
              Welcome to Baby Names App ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we handle your information when you use our baby name generator application.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            <p className="text-gray-700 mb-4">We collect the following types of information:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>App usage data (name generation history, category preferences)</li>
              <li>Purchase history for premium features</li>
              <li>Favorite names list (for premium users)</li>
              <li>Device information (iOS version, device model)</li>
              <li>App performance data</li>
            </ul>
            <p className="text-gray-700 font-semibold">We DO NOT collect:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Personal identification information</li>
              <li>Location data</li>
              <li>Contact information</li>
              <li>Photos or media</li>
              <li>Children's personal information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700">We use your information to:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Generate personalized name suggestions</li>
              <li>Save your favorite names (premium feature)</li>
              <li>Improve our name generation algorithm</li>
              <li>Maintain and improve app functionality</li>
              <li>Process in-app purchases</li>
              <li>Provide customer support</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">4. Data Storage and Security</h2>
            <ul className="list-disc pl-6 text-gray-700">
              <li>All app data is stored locally on your device</li>
              <li>Favorite names are synced using Apple's iCloud service</li>
              <li>We implement appropriate security measures to protect your data</li>
              <li>We do not sell or share your data with third parties</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">5. In-App Purchases</h2>
            <p className="text-gray-700">
              Our app offers in-app purchases. Payment information is processed entirely through Apple's App Store and we do not have access to your payment details.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">6. Analytics</h2>
            <p className="text-gray-700 mb-4">
              We use basic analytics to improve our service, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>App performance metrics</li>
              <li>Feature usage statistics</li>
              <li>Crash reports</li>
            </ul>
            <p className="text-gray-700 mt-4">
              All analytics data is anonymized and cannot be traced to individual users.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">7. Children's Privacy</h2>
            <p className="text-gray-700">
              Our app does not knowingly collect personal information from children under 13. All app features can be used without providing personal information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">8. Your Rights</h2>
            <p className="text-gray-700">You have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Access your stored data</li>
              <li>Request deletion of your data</li>
              <li>Opt out of analytics</li>
              <li>Contact us about privacy concerns</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy in the app and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">10. Data Deletion and Export</h2>
            <p className="text-gray-700">To delete your data:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Uninstall the app</li>
              <li>For premium users: cancel any active subscriptions through App Store settings</li>
              <li>Contact us for additional data deletion requests</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">11. Contact Us</h2>
            <p className="text-gray-700">
              If you have any questions about this privacy policy, please contact us at [Your Contact Email].
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">12. Legal Compliance</h2>
            <p className="text-gray-700">This app complies with:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Apple's App Store Guidelines</li>
              <li>GDPR (General Data Protection Regulation)</li>
              <li>CCPA (California Consumer Privacy Act)</li>
              <li>COPPA (Children's Online Privacy Protection Act)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">13. Third-Party Services</h2>
            <p className="text-gray-700">Our app uses the following third-party services:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Apple's StoreKit for in-app purchases</li>
              <li>iCloud for data synchronization</li>
              <li>Apple's CloudKit for data storage</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">14. Consent</h2>
            <p className="text-gray-700">
              By using our app, you consent to this privacy policy and our handling of your data as described above.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;